import { computed, reactive, ref } from 'vue';
import CardsAPI from 'custom/dashboard/api/cards';
import PipelinesAPI from 'custom/dashboard/api/pipelines';

const emptyColumn = () => ({ cards: [], count: 0, page: 0, request: 0 });

const byPosition = (a, b) => a.position - b.position;
const byNewestClosing = (a, b) => new Date(b.closed_at) - new Date(a.closed_at);

// The position to send for a card vuedraggable just placed at `newIndex` of `list`.
// The server removes the card from its old place and then inserts it at the given position
// (acts_as_list), so a card moving down its own column takes the place of the card now above
// it, and any other drop takes the place of the card now below it — or goes after the last
// card it was dropped under.
export const dropPosition = (list, { newIndex, oldIndex, sameStage }) => {
  const below = list[newIndex + 1];
  const above = list[newIndex - 1];
  if (sameStage && newIndex > oldIndex) return above.position;
  if (below) return below.position;
  return above ? above.position + 1 : 1;
};

// The board of one pipeline: its steps as columns, each fetching its own stage, so a busy
// stage paginates on its own instead of starving the others of a shared page.
export function useCardsBoard() {
  const pipelines = ref([]);
  const pipeline = ref(null);
  const stages = ref([]);
  const columns = reactive({});
  const isLoading = ref(true);

  const totalCount = computed(() =>
    stages.value.reduce(
      (total, stage) => total + (columns[stage.id]?.count || 0),
      0
    )
  );

  const loadPipelines = async () => {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data;
  };

  const selectPipeline = async id => {
    pipeline.value = pipelines.value.find(item => item.id === id);
    const { data } = await PipelinesAPI.getStages(id);
    stages.value = data;
    data.forEach(stage => {
      columns[stage.id] = emptyColumn();
    });
  };

  // Requests are numbered so a slow response for an outdated filter never lands last.
  const fetchColumn = async (stage, params, page = 1) => {
    const column = columns[stage.id];
    column.request += 1;
    const request = column.request;
    const { data } = await CardsAPI.get({
      ...params,
      stage_id: stage.id,
      page,
    });
    if (request !== column.request) return;

    const loadedIds = page === 1 ? [] : column.cards.map(card => card.id);
    column.cards = [
      ...(page === 1 ? [] : column.cards),
      ...data.payload.filter(card => !loadedIds.includes(card.id)),
    ];
    column.count = data.meta.count;
    column.page = page;
  };

  const fetchBoard = async (visibleStages, params) => {
    isLoading.value = true;
    await Promise.all(visibleStages.map(stage => fetchColumn(stage, params)));
    isLoading.value = false;
  };

  const loadMore = (stage, params) =>
    fetchColumn(stage, params, columns[stage.id].page + 1);

  // Mirrors on the loaded cards what acts_as_list did on the server: the source column closes
  // the gap the card left, the target column opens room where it landed.
  const shiftSiblings = (card, from, to) => {
    if (from.position !== null) {
      columns[from.stageId].cards.forEach(sibling => {
        if (sibling !== card && sibling.position > from.position)
          sibling.position -= 1;
      });
    }
    if (to.position !== null) {
      columns[to.stageId].cards.forEach(sibling => {
        if (sibling !== card && sibling.position >= to.position)
          sibling.position += 1;
      });
    }
  };

  // vuedraggable has already placed `card` in the target column's list; this persists the move
  // and settles the column on the order the server answered with. A move never changes the
  // status: a closed card changes step but has no place in the ordered list.
  const moveCard = async (card, { stageId, newIndex, oldIndex }) => {
    const from = { stageId: card.stage_id, position: card.position };
    const sameStage = from.stageId === stageId;
    const column = columns[stageId];
    const isOpen = card.status === 'open';
    if (!sameStage) {
      columns[from.stageId].count -= 1;
      column.count += 1;
    }

    const position = isOpen
      ? dropPosition(column.cards, { newIndex, oldIndex, sameStage })
      : undefined;
    const { data } = await CardsAPI.move(card.id, { stageId, position });

    shiftSiblings(card, from, { stageId, position: data.position });
    Object.assign(card, data);
    column.cards.sort(isOpen ? byPosition : byNewestClosing);
  };

  return {
    pipelines,
    pipeline,
    stages,
    columns,
    isLoading,
    totalCount,
    loadPipelines,
    selectPipeline,
    fetchBoard,
    loadMore,
    moveCard,
  };
}
